import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SERVICES, PRODUCTS } from '../utils/constants';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const HomePage = () => {
  useEffect(() => {
    // Simple animations on mount
    gsap.from('.hero h1', {
      duration: 0.8,
      y: 30,
      ease: 'power3.out',
    });

    gsap.from('.hero p', {
      duration: 0.8,
      y: 20,
      delay: 0.1,
      ease: 'power3.out',
    });

    gsap.from('.hero-buttons', {
      duration: 0.8,
      y: 20,
      delay: 0.2,
      ease: 'power3.out',
    });
  }, []);

  return (
    <>
      {/* Hero Section */}
      <section className="hero section">
        <div className="bg-shape bg-shape-1 floating"></div>
        <div className="bg-shape bg-shape-2 floating-delayed"></div>

        <div className="container hero-content">
          <h1>
            Transform Your Business with{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #1a5f8a, #0d3b5f)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Innovative IT Solutions
            </span>
          </h1>
          <p>
            We deliver cutting-edge software development, web applications, and enterprise
            solutions that drive growth and efficiency.
          </p>
          <div className="hero-buttons">
            <Link to="/services" className="btn btn-primary">
              Explore Services
            </Link>
            <Link to="/products" className="btn btn-secondary">
              View Products
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">What We Offer</span>
            <h2 className="section-title">Our Services</h2>
            <p className="section-description">
              Comprehensive IT solutions tailored to your business needs, delivered by expert
              teams with proven track records.
            </p>
          </div>

          <div className="services-grid">
            {SERVICES.map((service, index) => (
              <div key={service.id} className={`card reveal-fade hover-lift ${index > 0 ? `delay-${index * 100}` : ''}`}>
                <div className="card-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    {service.icon === 'code' && (
                      <>
                        <polyline points="16 18 22 12 16 6"></polyline>
                        <polyline points="8 6 2 12 8 18"></polyline>
                      </>
                    )}
                    {service.icon === 'monitor' && (
                      <>
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                        <line x1="8" y1="21" x2="16" y2="21"></line>
                        <line x1="12" y1="17" x2="12" y2="21"></line>
                      </>
                    )}
                    {service.icon === 'trending-up' && (
                      <>
                        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                        <polyline points="17 6 23 6 23 12"></polyline>
                      </>
                    )}
                    {service.icon === 'share-2' && (
                      <>
                        <circle cx="18" cy="5" r="3"></circle>
                        <circle cx="6" cy="12" r="3"></circle>
                        <circle cx="18" cy="19" r="3"></circle>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                      </>
                    )}
                  </svg>
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <Link to={service.link} className="btn btn-ghost">
                  Learn More →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="section" style={{backgroundColor: '#0F0F0F'}}>
        <div className="container">
          <div className="section-header">
            <span className="section-label">Our Products</span>
            <h2 className="section-title">Powerful SaaS Solutions</h2>
            <p className="section-description">
              Industry-leading software products designed to streamline operations and accelerate growth.
            </p>
          </div>

          <div className="grid grid-2">
            {PRODUCTS.map((product, index) => (
              <div key={product.id} className={`product-card reveal-scale ${index > 0 ? 'delay-200' : ''}`}>
                <div className="product-header">
                  <span className="product-badge" style={index > 0 ? {backgroundColor: 'var(--color-blue-light)'} : undefined}>
                    {index === 0 ? 'Featured Product' : 'New Release'}
                  </span>
                  <h3>{product.name}</h3>
                  <p style={{color: 'var(--color-gray)', fontSize: '1.125rem'}}>{product.tagline}</p>
                </div>
                
                <ul className="product-features">
                  {product.features.map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ul>
                
                <div style={{marginTop: 'var(--spacing-md)'}}>
                  <Link to={product.link} className="btn btn-primary" style={{ width: '100%' }}>
                    Learn More
                  </Link>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center" style={{marginTop: 'var(--spacing-lg)'}}>
            <p style={{fontSize: '1.125rem', color: 'var(--color-gray)', marginBottom: 'var(--spacing-md)'}}>Interested in our products? Get in touch for pricing and demos.</p>
            <Link to="/contact" className="btn btn-secondary">Contact Us for Inquiry</Link>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section id="about-preview" className="section">
        <div className="container">
          <div className="grid grid-2" style={{alignItems: 'center', gap: 'var(--spacing-xl)'}}>
            <div className="reveal-left">
              <span className="section-label">About SEVENHC</span>
              <h2>Building the Future of Digital Solutions</h2>
              <p style={{fontSize: '1.125rem', marginTop: 'var(--spacing-md)'}}>
                We are a team of passionate technologists, designers, and strategists committed to delivering exceptional IT solutions. With years of experience across multiple industries, we understand the challenges businesses face in the digital age.
              </p>
              <p style={{fontSize: '1.125rem'}}>
                Our mission is to empower organizations with technology that not only meets today's demands but anticipates tomorrow's opportunities. From startups to enterprise clients, we partner with businesses to drive innovation and achieve measurable results.
              </p>
              <div style={{marginTop: 'var(--spacing-md)'}}>
                <Link to="/about" className="btn btn-primary">Learn More About Us</Link>
              </div>
            </div>
            
            <div className="reveal-right">
              <div className="card" style={{padding: 'var(--spacing-xl)'}}>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--spacing-lg)'}}>
                  <div className="text-center">
                    <h3 style={{color: 'var(--color-blue-light)', fontSize: '3rem', marginBottom: '0.5rem'}}>50+</h3>
                    <p style={{marginBottom: 0, color: 'var(--color-gray-dark)'}}>Projects Delivered</p>
                  </div>
                  <div className="text-center">
                    <h3 style={{color: 'var(--color-blue-light)', fontSize: '3rem', marginBottom: '0.5rem'}}>30+</h3>
                    <p style={{marginBottom: 0, color: 'var(--color-gray-dark)'}}>Happy Clients</p>
                  </div>
                  <div className="text-center">
                    <h3 style={{color: 'var(--color-blue-light)', fontSize: '3rem', marginBottom: '0.5rem'}}>10+</h3>
                    <p style={{marginBottom: 0, color: 'var(--color-gray-dark)'}}>Years Experience</p>
                  </div>
                  <div className="text-center">
                    <h3 style={{color: 'var(--color-blue-light)', fontSize: '3rem', marginBottom: '0.5rem'}}>24/7</h3>
                    <p style={{marginBottom: 0, color: 'var(--color-gray-dark)'}}>Support Available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section" style={{background: 'linear-gradient(135deg, #0A0A0A, #1A1A1A)', position: 'relative', overflow: 'hidden'}}>
        <div className="bg-shape bg-shape-3 pulse"></div>
        
        <div className="container text-center" style={{position: 'relative', zIndex: 2}}>
          <h2 className="reveal-fade" style={{marginBottom: 'var(--spacing-md)'}}>Ready to Transform Your Business?</h2>
          <p className="reveal-fade delay-100" style={{fontSize: '1.25rem', maxWidth: '700px', margin: '0 auto var(--spacing-lg)'}}>
            Let's discuss how our IT solutions can help you achieve your business goals. Get in touch with our team today.
          </p>
          <div className="reveal-fade delay-200">
            <Link to="/contact" className="btn btn-primary" style={{padding: '1.25rem 3rem', fontSize: '1.125rem'}}>Get Started</Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
